import { useI18n } from '../../../i18n';
// ============================================
// NodeDirectoryTable Component
// Store address table with search, filters, and inline actions
// ============================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  StoresIcon,
  WarehouseIcon,
  EditIcon,
  TrashIcon,
  CheckCircleIcon,
} from '../../../components/icons';
import { Pagination } from '../../../components/shared';
import { SelectDropdown } from '../../../components/shared/SelectDropdown';
import { cn } from '../../../utils/cn';

// ============================================
// Types
// ============================================

export type FacilityType = 'STORE' | 'WAREHOUSE';
export type FacilityStatus = 'ONLINE' | 'MAINTENANCE' | 'OFFLINE';

export interface StoreTableRow {
  id: string;
  code: string;
  name: string;
  type: FacilityType;
  address: string;
  city: string;
  status: FacilityStatus;
  /**
   * Ảnh đại diện cửa hàng — ảnh ĐẦU TIÊN trong `Store.image_urls`.
   *
   * Tuỳ chọn: phần lớn cửa hàng cũ chưa gắn ảnh nào, và đó là bình thường chứ
   * không phải thiếu dữ liệu. Thiếu ảnh thì vẽ ô giữ chỗ cùng kích thước để
   * cột tên không so le giữa các dòng.
   */
  imageUrl?: string | null;
}

/**
 * Ảnh vuông 36px cho một dòng cửa hàng.
 *
 * Ba trạng thái, tất cả CÙNG KÍCH THƯỚC để hàng không nhảy: có ảnh, không có
 * ảnh, và ảnh hỏng link. Ca thứ ba đáng xử lý riêng — ảnh upload rồi bị xoá
 * khỏi CDN sẽ để lại icon "ảnh vỡ" mặc định của trình duyệt, xấu và trông như
 * lỗi giao diện. `onError` đổi sang ô giữ chỗ, giống hệt cửa hàng chưa có ảnh.
 */
function StoreThumbnail({ url, name }: { url?: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  const show = url && !failed;

  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
      {show ? (
        <img
          src={url}
          alt={name}
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        // Chữ cái đầu thay cho icon chung: nó khác nhau giữa các dòng nên vẫn
        // giúp mắt định vị, trong khi một icon giống hệt ở mọi dòng thì không.
        <span className="text-xs font-bold uppercase text-slate-400">
          {name.trim().charAt(0) || '?'}
        </span>
      )}
    </div>
  );
}

interface NodeDirectoryTableProps {
  data: StoreTableRow[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onFilter?: () => void;
  onExport?: () => void;
  onRowClick?: (storeId: string) => void;
  onView?: (storeId: string) => void;
  onEdit?: (storeId: string) => void;
  onDelete?: (storeId: string) => void;
  isLoading?: boolean;
  className?: string;
}

// ============================================
// Status Badge
// ============================================

function StatusBadge({ status }: { status: FacilityStatus }) {
  const { t } = useI18n();
  const styles: Record<FacilityStatus, string> = {
    ONLINE: 'bg-[#DCFCE7] text-[#15803D]',
    MAINTENANCE: 'bg-[#FFDBCD] text-[#943700]',
    OFFLINE: 'bg-red-100 text-red-700',
  };
  const labels: Record<FacilityStatus, string> = {
    ONLINE: t('active'),
    MAINTENANCE: t('maintenance'),
    OFFLINE: t('inactive'),
  };

  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold', styles[status])}>
      {labels[status]}
    </span>
  );
}

// ============================================
// Type Badge
// ============================================

function TypeBadge({ type }: { type: FacilityType }) {
  const { t } = useI18n();
  if (type === 'STORE') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ backgroundColor: '#E1F5FE', color: '#0288D1' }}>
        <StoresIcon size={12} />
        {t('storeType')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ backgroundColor: '#D4EDDA', color: '#219653' }}>
      <WarehouseIcon size={12} />
      {t('warehouse')}
    </span>
  );
}

// ============================================
// Main Component
// ============================================

export function NodeDirectoryTable({
  data,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onRowClick,
  onEdit,
  onDelete,
  isLoading,
  className,
}: NodeDirectoryTableProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const totalPages = Math.ceil(totalCount / pageSize);

  const getEffectiveCity = (r: typeof data[0]) => {
    if (r.city && r.city !== '-') return r.city;
    if (r.address) {
      const parts = r.address.split(',');
      const lastPart = parts[parts.length - 1]?.trim();
      if (lastPart) {
        const cityMatch = lastPart.match(/[a-zA-Z\u00C0-\u017F\s]+$/);
        if (cityMatch && cityMatch[0].trim()) {
          return cityMatch[0].trim();
        }
        return lastPart;
      }
    }
    return '';
  };

  const filteredData = data.filter((row) => {
    const matchSearch =
      !search ||
      (row.name?.toLowerCase().includes(search.toLowerCase()) ?? false) ||
      (row.code?.toLowerCase().includes(search.toLowerCase()) ?? false) ||
      (row.address?.toLowerCase().includes(search.toLowerCase()) ?? false);
    const rowCity = getEffectiveCity(row);
    const matchLocation = !locationFilter || rowCity === locationFilter;
    const matchStatus = !statusFilter || row.status === statusFilter;
    return matchSearch && matchLocation && matchStatus;
  });

  const uniqueCities = Array.from(
    new Set(
      data.map((r) => getEffectiveCity(r)).filter(Boolean)
    )
  );

  const handleRowClick = (storeId: string) => {
    if (onRowClick) {
      onRowClick(storeId);
    } else {
      navigate(`/stores/${storeId}`);
    }
  };

  return (
    <div
      className={cn('rounded-xl bg-white shadow-sm', className)}
      style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-6 py-4">
        {/* Search */}
        <div className="relative w-full min-w-0 sm:w-auto sm:flex-1 sm:min-w-[200px] sm:max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
            <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder={t('storesSearchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            enterKeyHint="search"
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 placeholder-slate-400 focus:border-primary-400 focus:outline-none"
          />
        </div>

        <div className="grid w-full grid-cols-2 gap-3 sm:ml-auto sm:flex sm:w-auto sm:items-center">
          {/* Location filter */}
          <SelectDropdown
            size="sm"
            value={locationFilter}
            onChange={setLocationFilter}
            options={[
              { value: '', label: t('allLocations') },
              ...uniqueCities.map((city) => ({ value: city, label: city })),
            ]}
            className="min-w-0 sm:min-w-[150px]"
          />

          {/* Status filter */}
          <SelectDropdown
            size="sm"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: '', label: t('allStatus') },
              { value: 'ONLINE', label: t('storesActive') },
              { value: 'MAINTENANCE', label: t('maintenance') },
              { value: 'OFFLINE', label: t('storesInactive') },
            ]}
            className="min-w-0 sm:min-w-[140px]"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.6px] text-slate-500">{t('code')}</th>
              <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.6px] text-slate-500">{t('storeName')}</th>
              <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.6px] text-slate-500">{t('type')}</th>
              <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.6px] text-slate-500">{t('address')}</th>
              <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.6px] text-slate-500">{t('storesStatus')}</th>
              <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-bold uppercase tracking-[0.6px] text-slate-500">{t('storesActions')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  {/* `h-9` khớp chiều cao ảnh đại diện ở dòng thật — thiếu nó
                      thì cả bảng nhảy một nhịp lúc tải xong. */}
                  {Array.from({ length: 6 }).map((_, colIdx) => (
                    <td key={colIdx} className="px-6 py-4">
                      <div className="flex h-9 items-center">
                        <div className="h-4 w-20 animate-pulse rounded bg-slate-200" />
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-sm text-slate-500">
                  No stores found
                </td>
              </tr>
            ) : (
              filteredData.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => handleRowClick(row.id)}
                  className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50"
                >
                  <td className="px-6 py-4 text-sm font-semibold text-slate-800">{row.code}</td>
                  {/* Ảnh đi KÈM ô tên, không tách thành cột riêng: nó là
                      thuộc tính nhận dạng của cửa hàng chứ không phải một
                      trường dữ liệu để so sánh giữa các dòng — thêm hẳn một cột
                      chỉ để chứa ảnh sẽ ép cột Address hẹp lại mà không đổi
                      được gì. */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <StoreThumbnail url={row.imageUrl} name={row.name} />
                      <span className="text-sm text-slate-700">{row.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <TypeBadge type={row.type} />
                  </td>
                  <td className="max-w-[200px] truncate px-6 py-4 text-sm text-slate-500">{row.address}</td>
                  <td className="px-6 py-4">
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {onEdit && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onEdit(row.id); }}
                          className="flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                          title={t('storesEdit')}
                        >
                          <EditIcon size={15} />
                        </button>
                      )}
                      {onDelete && (
                        row.status === 'OFFLINE' ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); onDelete(row.id); }}
                            className="flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-green-50 hover:text-green-600"
                            title={t('storesActivate') || 'Activate'}
                          >
                            <CheckCircleIcon size={15} />
                          </button>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); onDelete(row.id); }}
                            className="flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
                            title={t('storesDelete')}
                          >
                            <TrashIcon size={15} />
                          </button>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        variant="numbered"
        currentPage={currentPage}
        totalPages={!!search || !!locationFilter || !!statusFilter ? Math.max(1, Math.ceil(filteredData.length / pageSize)) : totalPages}
        totalItems={!!search || !!locationFilter || !!statusFilter ? filteredData.length : totalCount}
        pageSize={pageSize}
        onPageChange={onPageChange}
        itemLabel="stores"
      />
    </div>
  );
}
